import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { FIELD_LABEL_CLASS, SelectChevron, fieldClass } from "@charitystack/shared";

const isSafari =
  typeof navigator !== "undefined" &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export default function FundsDropdown({
  funds,
  selectedFund,
  setSelectedFund,
  elementID,
  brandColors,
  hasError,
  onFundChange,
  containerClassName = "w-full pt-2",
}) {
  // Safari fix: after each selection, increment key to force React to destroy
  // and recreate the <select> DOM node. This resets Safari's stale internal
  // focus state that causes the double-click-to-reopen bug.
  const [selectKey, setSelectKey] = useState(0);
  // A11-003: how the current change was made. Only a pointer pick needs the
  // blur/remount below; a keyboard or assistive-tech change must keep focus on
  // the control, or the donor is dropped onto <body> mid-form. Consumed by
  // each change, so a pointer pick cannot mark the next, event-less change.
  const lastInputRef = useRef(null);
  const selectRef = useRef(null);
  const refocusAfterRemountRef = useRef(false);
  // A11-003 (keyboard-only donor journey): the overlay renders the form twice
  // (desktop pane and mobile drawer), so an id built from elementID alone was
  // duplicated and the visible select's label bound to the hidden copy. The
  // per-instance infix keeps every id unique while keeping the elementID prefix
  // and the `-selected-fund` suffix that selectors match on.
  const idBase = `${elementID}-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const selectId = `${idBase}-selected-fund`;

  // Safari's remount (keyboard path) replaces the focused node; put focus back
  // on its replacement so the keyboard change still leaves focus in place.
  useLayoutEffect(() => {
    if (refocusAfterRemountRef.current) {
      refocusAfterRemountRef.current = false;
      selectRef.current?.focus({ preventScroll: true });
    }
  }, [selectKey]);

  // Donor redesign (CON-515, SPEC §C Inputs): a field is white always, so the
  // old brand-tinted ground is gone and `brandColors` no longer paints here.
  void brandColors;

  const handleChange = (event) => {
    setSelectedFund(event.target.value);
    if (onFundChange && event.target.value !== "") {
      onFundChange();
    }
    const pointerInitiated = lastInputRef.current === "pointer";
    lastInputRef.current = null;
    if (isSafari) {
      // Remount THIS select to reset its stale internal state
      refocusAfterRemountRef.current = !pointerInitiated;
      setSelectKey((k) => k + 1);
    }
    if (pointerInitiated) {
      // Blur so the next click is a fresh open gesture (all browsers).
      const changedSelect = event.target;
      setTimeout(() => {
        changedSelect.blur();
      }, 0);
    }
  };

  return (
    <div
      className={`${containerClassName} funds-dropdown-container`}
      id={`${idBase}-funds`}
    >
      <label
        htmlFor={selectId}
        className={`block pb-[var(--cs-space-2)] ${FIELD_LABEL_CLASS}`}
      >
        Fund
      </label>
      <div className="relative">
        <select
          key={isSafari ? selectKey : undefined}
          ref={selectRef}
          onPointerDown={() => {
            lastInputRef.current = "pointer";
          }}
          onKeyDown={() => {
            lastInputRef.current = "keyboard";
          }}
          onChange={handleChange}
          // The shared field recipe (SPEC §C Inputs; Paper 66D-0 "Select"):
          // 48 tall, radius 12, white, 1px edge (strong on hover), accent
          // border + 1px inset on focus, the error edge when invalid.
          className={`dynamic-input-text block h-[var(--cs-input-height)] cursor-pointer appearance-none pl-[var(--cs-input-padding-x)] pr-10 ${fieldClass(hasError)}`}
          style={{
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            appearance: 'none',
            backgroundImage: 'none',
            WebkitTextFillColor: 'inherit',
          }}
          value={selectedFund}
          id={selectId}
        >
          <option value="" disabled hidden></option>
          {funds.map((fund, i) => (
            <option
              className="dynamic-input-text w-full"
              key={i}
              value={fund}
              id={`${idBase}-selectable-fund-${i}`}
            >
              {fund}
            </option>
          ))}
        </select>
        <SelectChevron inField id={`${idBase}-funds-chevron-down-icon`} />
      </div>
    </div>
  );
}
